import Link from "next/link";
import { useFormContext } from "react-hook-form";
import { Box, Checkbox, FormControlLabel, Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";
import { InputError } from "src/components/base/components/FormComponents/FormComponents";

import { routes } from "src/shared/routes";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "What is the Big A Pass?",
    answer:
      'The "Big A Pass" is a monthly subscription membership that gets you access to most Angels regular season home games. 7-15 games per month are available, and tickets can be claimed in the MLB Ballpark app leading up to each game.',
  },
  {
    question: "Is a seat guaranteed for every game?",
    answer:
      "Opening Day, Dodgers, Yankees, and the 4th of July Red Sox games are not included. For all other games, view level or outfield seats are assigned based on availability, meaning you'll have a different seat for each game you attend.",
  },
  {
    question: "How do I claim my tickets?",
    answer:
      "Check-in opens at 1 pm the day before the start of each homestand. You will be notified via text when the period is open. Tickets can be claimed until 3 hours prior to game time.",
  },
  {
    question: "Where will my seat be located?",
    answer:
      "Seats will be assigned to either the view level or outfield, based on availability.",
  },
  {
    question: "Can I add additional tickets for guests?",
    answer:
      "Yes! Additional guest tickets are available for purchase at check-in, and need to be purchased no later than 3 hours prior to game time.",
  },
  {
    question: "How do I upgrade my seats?",
    answer:
      'After checking in, you\'ll have the opportunity to purchase upgraded tickets. Simply select the "Add-Ons" option below your game to see all available upgrades, subject to availability.',
  },
  {
    question: "When are my tickets delivered?",
    answer:
      "After checking-in, tickets will be delivered to your MLB Ballpark app at 3 hours prior to gametime. If you are unable to view your tickets, try logging out and back into the MLB app.",
  },
  {
    question: "How do I enter the venue?",
    answer:
      "Present your barcoded mobile tickets in the MLB Ballpark for scanning at the gates.",
  },
  {
    question: "Can I purchase multiple Passes?",
    answer:
      "Yes! For example, a family of 4 should have one person purchase 4 Passes, then 4 seats will be delivered together to that person's Ballpark app 3 hours prior to each game.",
  },
  {
    question: "Can I bring different people with me to each game?",
    answer:
      "Yes! Different people can attend each game, but the primary account holder must be present as tickets are only accessible via their MLB Ballpark App.",
  },
  {
    question:
      "Can I request to be seated with friends who are also Big A Passholders?",
    answer:
      "Yes! You can create a group and invite friends to sit together in the Big A Pass menu. All Passholders in the group must accept the invitation then check in at least 3 hours prior to gametime to be seated together.",
  },
  {
    question: "Can Big A Pass tickets be transferred?",
    answer:
      "Passes and Pass tickets cannot be shared/forwarded to other parties. Admission into the ballpark will be managed through the MLB Ballpark app on the Passholder's mobile device.",
  },
  {
    question:
      "Can I sell my Big A Pass tickets for games that I do not attend?",
    answer:
      "No. Big A Pass tickets are non-transferable. Passes and Pass tickets cannot be resold. Violators will be removed from the program.",
  },
  {
    question: "Can I print my ticket?",
    answer:
      "No. Printed tickets and screenshots will not be accepted at the gates for entry. All you need is the mobile ticket in the MLB Ballpark app on your phone.",
  },
  {
    question: "What if I need ADA seating?",
    answer:
      "ADA accommodations can be requested by emailing tickets@angels.com at least 3 hours prior to gametime with the specific number of wheelchair spaces, companion seats, and/or semi-ambulatory seats required. Alternatively, ticketed guests may stop by our Advanced Ticket Windows once inside the stadium for exchange, based on availability.",
  },
  {
    question: "What happens if a game time changes?",
    answer:
      "All game times are subject to change. Your Pass ticket will be valid only for the date of the game and is non-exchangeable.",
  },
  {
    question: "What if one of my games is rained out or postponed?",
    answer:
      "If a game is postponed your Pass ticket will be valid for the rescheduled game only.",
  },
  {
    question: "Does the Big A Pass include postseason access?",
    answer:
      "No, the Big A Pass is not considered a season plan and does not guarantee any postseason access.",
  },
  {
    question: "Can I cancel my subscription?",
    answer: (
      <>
        Yes. Your subscription will auto renew for the following month on the
        26th, but you may cancel your renewal between the 15th and the 25th by
        clicking{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "losangelesangels_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        in the Big A Pass menu in the MLB Ballpark app. Access will remain until
        the end of the current calendar month and there are no partial refunds.
        <br />
        <br />
        For any other questions, please email tickets@angels.com or call
        714-4ANGELS during regular business hours.
      </>
    ),
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    In connection with your purchase, you will receive ticket-related commercial
    emails from angels.com . You may unsubscribe at any time. By checking this
    box you agree to receive additional commercial emails from angels.com and
    MLB.com. The terms and conditions of your ticket and policies applicable to
    its use are updated to address circumstances related to COVID-19 and health
    and safety. By clicking the Checkout button, you agree to accept all Terms
    and Conditions. Further, due to the COVID-19 pandemic, scheduling may be
    impacted; games may be canceled, rescheduled, or postponed or games may be
    held with limited capacity. Upon completing your purchase by selecting
    Checkout you acknowledge and agree that your credit card will be charged. By
    clicking Checkout, you agree to the{" "}
    <Link
      href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
      target="_blank"
    >
      MLB Privacy Policy
    </Link>
    ,{" "}
    <Link
      href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
      target="_blank"
    >
      Terms of Use
    </Link>{" "}
    and to receive your ticket(s) via an SMS text message to the mobile #
    associated with your Angels account. Message and data rates may apply.
    Tickets will be made available in the MLB Ballpark mobile application only.
    MLB.com and supported mobile device required.
  </>
);

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 1,
      label: "Big A Pass Terms and Conditions | Los Angeles Angels",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/angels/tickets/specials/big-a-pass/terms-and-conditions ",
          "_self",
        );
      },
    },
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self",
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self",
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/angels/tickets/ticketback",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        General Ticketing Terms & Conditions | Los Angeles Angels
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
{
  TITLE: () => (
    <>
      You&apos;e all set to experience the action! Don&apos;t forget to
      confirm attendance here on game day!
    </>
  ),
  DESCRIPTION: () => {
    const router = useRouterExt();
    return (
      <>
        Friends don&apos;t leave friends behind.{" "}
        <b>
          <u>
            <span
              onClick={() => router.pushSchema(routes.my_group)}
              style={{ cursor: "pointer" }}
            >
              Invite your friends
            </span>
          </u>
        </b>{" "}
        and enjoy games together.
      </>
    );
  },
};

export function CheckoutTermsCheckbox() {
  const theme = useSSTheme();
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const primaryFieldName = "terms";
  const secondaryFieldName = "secondaryTerms";
  const errorMessage = errors[primaryFieldName]?.message as string;
  const secondaryErrorMessage = errors[secondaryFieldName]?.message as string;

  return (
    <Box>
      <Box>
        <FormControlLabel
          sx={{
            color: theme.colors.gray[200],
            alignItems: "flex-start",
            py: 0.5,
            transition: "all 0.3s ease",
            margin: 0,

            "&:hover": {
              backgroundColor: theme.colors.gray[50],
              borderRadius: "4px",
            },
          }}
          label={
            <SSText variant="caption" fontSize={"0.9rem"}>
              By clicking Purchase Pass below, you agree to the{" "}
              <Link
                target="_blank"
                href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
              >
                MLB Terms of Use
              </Link>{" "}
              and{" "}
              <Link
                target="_blank"
                href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
              >
                Privacy policy
              </Link>
              , as well as Angels Baseball{" "}
              <Link
                target="_blank"
                href="https://www.mlb.com/app/ballpark/angels/tickets/specials/big-a-pass/terms-and-conditions"
              >
                Terms & Conditions
              </Link>{" "}
              and you have read and understand the product details and FAQ at{" "}
              <Link target="_blank" href="https://pass.attendevents.com/losangelesangels_pass/faqs">
                Big A Pass | Los Angeles Angels
              </Link>
              .
            </SSText>
          }
          control={
            <Checkbox
              size="medium"
              disableRipple
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: "1.8rem",
                  marginTop: "-8px",
                  marginLeft: "-6px",
                },
              }}
              {...register("terms", {
                required: "Please accept the terms and conditions",
              })}
            />
          }
        />
        {errorMessage ? <InputError>{errorMessage}</InputError> : null}
      </Box>

      <Box>
        <FormControlLabel
          sx={{
            color: theme.colors.gray[200],
            alignItems: "flex-start",
            py: 0.5,
            transition: "all 0.3s ease",
            margin: 0,

            "&:hover": {
              backgroundColor: theme.colors.gray[50],
              borderRadius: "4px",
            },
          }}
          label={
            <SSText variant="caption" fontSize={"0.9rem"}>
              By checking this box, I agree to receive automated texts from
              Angels Baseball LP, including promotional, informational, and/or
              transactional information. Message and data rates may apply. By
              agreeing to automated text messages, you agree to these{" "}
              <Link
                target="_blank"
                href="https://www.mlb.com/app/ballpark/angels/tickets/specials/big-a-pass/terms-and-conditions"
              >
                Terms & Conditions
              </Link>
              . Message frequency varies. You may opt-out at any time.
            </SSText>
          }
          control={
            <Checkbox
              size="medium"
              disableRipple
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: "1.8rem",
                  marginTop: "-8px",
                  marginLeft: "-6px",
                },
              }}
              {...register(secondaryFieldName, {
                required: "Please accept the terms and conditions",
              })}
            />
          }
        />
        {secondaryErrorMessage ? (
          <InputError>{secondaryErrorMessage}</InputError>
        ) : null}
      </Box>
    </Box>
  );
}
